import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  Button,
  useMultiStepForm,
  FormToggleGroup,
} from "web-design-system";
import { BANK_ACCOUNT_TYPE } from "creso-core/constants";
import {
  useUpdateInvestorBankDetails,
  useCreateInvestorBankDetails,
  useInvestorPrimaryBankAccount,
  useVerifyBankDetails,
  useInvestorProfile,
  useBankServiceCapabilitiesByIfsc,
} from "creso-core/hooks";
import { BankDetailsSchema } from "creso-core/zod";
import {
  getBankServiceCapabilityWarningMessage,
  sleep,
} from "creso-core/utils";
import {
  CheckCheckIcon,
  InfoIcon,
  Loader2,
  TriangleAlertIcon,
} from "lucide-react";
import { useState } from "react";
import { Client } from "creso-core/types";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { AutofilledData } from "./autofilled-data";

const MAX_ATTEMPTS = 3;

export function BankDetails() {
  const { client } = useClientOnboarding();
  const { previousStep, nextStep } = useMultiStepForm();
  const investmentAccountGuid = client?.investment_account ?? "";
  const { data: bankDetails, refetch } = useInvestorPrimaryBankAccount(
    investmentAccountGuid
  );

  const { data: profile } = useInvestorProfile(
    { investment_account_guid: investmentAccountGuid ?? "" },
    {
      enabled: true,
    }
  );

  const { mutateAsync: createBankDetails } = useCreateInvestorBankDetails();

  const { mutateAsync: updateBankDetails } = useUpdateInvestorBankDetails();

  const { mutateAsync: verifyBankDetails } = useVerifyBankDetails();

  const form = useForm<z.infer<typeof BankDetailsSchema>>({
    resolver: zodResolver(BankDetailsSchema),
    values: {
      accountNumber: bankDetails?.account_number ?? "",
      ifsc: bankDetails?.ifsc_code ?? "",
      type: bankDetails?.type || BANK_ACCOUNT_TYPE[0].value,
      confirmAccountNumber: bankDetails?.account_number ?? "",
      primaryAccountHolderName:
        profile?.name ||
        bankDetails?.primary_account_holder_name ||
        client?.name ||
        "",
    },
  });

  const ifscCode = form.watch("ifsc");
  const { data: bankServiceCapabilities } = useBankServiceCapabilitiesByIfsc(
    ifscCode,
    {
      enabled: ifscCode.length >= 11,
    }
  );

  const [isVerifying, setIsVerifying] = useState(false);

  const onSubmit = async (data: z.infer<typeof BankDetailsSchema>) => {
    const payload = {
      account_number: data.accountNumber,
      ifsc_code: data.ifsc,
      type: data.type,
      investment_account_guid: investmentAccountGuid,
      primary_account_holder_name: data.primaryAccountHolderName,
      autofilled: bankDetails?.autofilled ? !isDirty : false,
    };

    try {
      setIsVerifying(true);
      if (bankDetails?.id) {
        await updateBankDetails(payload);
      } else {
        await createBankDetails(payload);
      }
      try {
        await verifyBankDetails(investmentAccountGuid);
        let details = await refetch();
        let attempts = 1;
        while (
          details.data?.verification_status === "unverified" &&
          attempts <= MAX_ATTEMPTS
        ) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(5000);
          // eslint-disable-next-line no-await-in-loop
          details = await refetch();

          attempts += 1;
        }
      } catch {
        // Ingore error since we display this inline
      }
    } catch (error) {
      handleError(error, "Failed to update bank details");
    } finally {
      setIsVerifying(false);
    }
    return true;
  };

  const { isDirty, isSubmitted } = form.formState;

  const bankAccountStatus = bankDetails?.verification_status;

  const showVerifyButton =
    isDirty ||
    !bankDetails?.bank_account_guid ||
    isVerifying ||
    (!isSubmitted && bankAccountStatus !== "verified");

  const showSkipButton =
    !showVerifyButton && isSubmitted && bankAccountStatus !== "verified";

  const showNextButton =
    !showVerifyButton && !showSkipButton && bankAccountStatus === "verified";

  const bankCapabilityWarningMessage = getBankServiceCapabilityWarningMessage(
    bankServiceCapabilities
  );

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Bank details</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="accountNumber"
            render={({ field }) => (
              <FormInput
                disabled={isVerifying}
                label="Account number"
                placeholder="1234567890"
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="confirmAccountNumber"
            render={({ field }) => (
              <FormInput
                disabled={
                  !form.getFieldState("accountNumber").isDirty || isVerifying
                }
                label="Confirm account number"
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="ifsc"
            render={({ field }) => (
              <FormInput
                disabled={isVerifying}
                placeholder="SBIN0000489"
                label="IFSC code"
                {...field}
                onChange={(value) => {
                  field.onChange(value?.toLocaleUpperCase() ?? "");
                }}
              />
            )}
          />
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormToggleGroup
                disabled={isVerifying}
                label="Account type"
                options={BANK_ACCOUNT_TYPE.map((type) => ({
                  label: type.label,
                  value: type.value,
                }))}
                {...field}
              />
            )}
          />
          <div className="mt-auto flex w-full text-foreground">
            {!isVerifying &&
              !isDirty &&
              bankDetails?.verification_status === "failed" && (
                <div className="flex gap-2 p-3 w-full text-sm leading-5 rounded-lg bg-tag-orange text-tag-orange-foreground items-start">
                  <TriangleAlertIcon className="size-4 flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold">Verification Failed</span>
                    <span className="text-sm">
                      {bankDetails?.verification_remark}
                    </span>
                  </div>
                </div>
              )}
            {!isVerifying &&
              !isDirty &&
              bankDetails?.verification_status === "verified" && (
                <div className="flex flex-col gap-1.5 px-1.5 py-3 w-full text-sm items-center leading-5 rounded-lg bg-tag-green text-tag-green-foreground">
                  <div className="flex gap-2 items-center justify-start w-full px-1.5">
                    <CheckCheckIcon className="size-4 text-tag-green-foreground flex-shrink-0" />
                    <span className="font-semibold">Verified</span>
                  </div>
                  {bankCapabilityWarningMessage ? (
                    <div className="flex flex-row gap-2 items-center w-full px-2">
                      <InfoIcon className="size-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {bankCapabilityWarningMessage}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            {isVerifying && (
              <div className="flex flex-col w-full text-sm leading-5 gap-2 bg-tag-slate text-tag-slate-foreground rounded-lg p-3">
                <div className="flex gap-2 items-center">
                  <Loader2 className="size-4 animate-spin flex-shrink-0" />
                  <span className="font-semibold tracking-tight">
                    Account will be verified by sending ₹1
                  </span>
                </div>
                <span>
                  Verifying account details. This may take upto 15 seconds.
                </span>
              </div>
            )}
            {!isVerifying &&
              isSubmitted &&
              bankAccountStatus === "unverified" &&
              !form.formState.isDirty && (
                <div className="flex flex-row items-center text-sm gap-2 bg-tag-slate text-tag-slate-foreground rounded-lg p-3 leading-5">
                  <InfoIcon className="size-4 flex-shrink-0" />
                  <span>
                    Verification is taking longer than expected. We&apos;ll do
                    this for you in the background.
                  </span>
                </div>
              )}
          </div>
          <DialogFooter className="flex sm:flex-col gap-4">
            <AutofilledData
              client={client as Client}
              autofillSource={!isDirty ? bankDetails?.autofill_source : null}
            />
            <div className="flex items-center w-full gap-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              {/** If verification is not triggered, show the Verify button */}
              {showVerifyButton && (
                <Button
                  className="w-full flex-1"
                  type="submit"
                  disabled={isVerifying}
                >
                  Verify
                </Button>
              )}
              {/** If verification hasn't completed in 15s, show the skip button */}
              {showSkipButton && (
                <Button
                  className="w-full flex-1"
                  type="button"
                  onClick={nextStep}
                >
                  Skip
                </Button>
              )}
              {/** Show the next button when
               * status is verified and form is not dirty
               * form is submitted for verification but the status is still unverified (timeout)
               */}
              {showNextButton && (
                <Button
                  className="w-full flex-1"
                  type="button"
                  onClick={nextStep}
                >
                  Next
                </Button>
              )}
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
